import {z} from "zod"

import {StrapiIdType, StrapiItemMeta} from "../../@reditor/core/strapi"

import {Content, ContentLink} from "../content"

/** @typedef {z.infer<typeof GlobalAttributes>} GlobalAttributes */
export const GlobalAttributes = z.object({
  author: z.string().optional().nullable(),
  siteTitle: z.string().optional().nullable(),
  siteDescription: z.string().optional().nullable(),
  contactEmail: z.string().optional().nullable(),
  contactLocation: z.string().optional().nullable(),
  contactPhone: z.string().optional().nullable(),
  contents: z
    .object({
      data: z.array(Content).nullable(),
    })
    .transform(o => o.data),
})

/** @typedef {z.infer<typeof HeaderSubLinks>} HeaderSubLinks */
export const HeaderSubLinks = z.array(ContentLink).optional().default([])

/** @typedef {ReturnType<typeof getNavBarFromGlobal>} GlobalNavBar */

/**
 * @param {Global=} globalResult
 * @param {boolean=} preview
 */
export const getNavBarFromGlobal = (globalResult, preview = false) => {
  if (!globalResult?.contents)
    return {
      logo: undefined,
      links: [],
    }
  const contents = globalResult.contents

  const headerContent = contents.find(d => d?.name === "header-navbar")

  const headerLinksContent = contents
    .filter(d => d?.type === "header-navbar")
    .sort((a, b) => (a?.name ?? "").localeCompare(b?.name ?? ""))

  return {
    logo: headerContent?.image,
    whiteLogo: headerContent?.images[0]?.url,
    links: headerLinksContent.map(d => {
      const subLinks = HeaderSubLinks.parse(d.data?.links ?? [])
      return {
        title: d.title,
        hidden: d.hidden,
        subLinks: preview
          ? subLinks.map(subLink => ({
              ...subLink,
              url: subLink.url?.startsWith("/")
                ? `/preview${subLink.url}`
                : subLink.url,
            }))
          : subLinks,
      }
    }),
  }
}


/**
 * @param {Global=} globalResult
 * @param {boolean=} preview
 */
export const getFooterFromGlobal = (globalResult, preview = false) => {
	const contents = globalResult.contents
	const footerLinksContent = contents
    .filter(d => d?.type === "footer")
    .sort((a, b) => (a?.name ?? "").localeCompare(b?.name ?? ""))
	return {
		footerLinksContent
	}
}

/** @typedef {z.infer<typeof Global>} Global */
export const Global = z
  .object({
    id: StrapiIdType,
    attributes: GlobalAttributes,
    meta: StrapiItemMeta.optional().nullable(),
  })
  .transform(({id, attributes, ...obj}) => ({
    ...obj,
    id,
    ...attributes,
  }))

/** @typedef {z.infer<typeof GetGlobalParams>} GetGlobalParams */
export const GetGlobalParams = StrapiIdType

/** @typedef {z.infer<typeof GetGlobalResult>} GetGlobalResult */
export const GetGlobalResult = z
  .object({
    data: Global,
    meta: z.object({}).optional(),
  })
  .transform(({data, ...obj}) => ({
    ...obj,
    ...data,
  }))
